import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";

import { getJrVersions } from "./jrV2Slice";
import { Colors } from "../../../assets/Colors";

// Perbandingan dua versi JR (hasil Recall). Data tiap versi berbentuk
// { header, lines, details } — mentah dari tabel JrHeader/JrLine/JrLineDetail.
// Item (JrLine) dicocokkan per JrLineID (stabil lintas versi). Entri activity
// (JrLineDetail) disimpan ulang tiap kali editor di-save sehingga DetailID-nya
// berganti; entri dicocokkan berdasarkan ISI-nya (lih. entrySig).

const STATUS_LABELS = {
  "-1": "Draft", 99: "Menunggu Manifest", 100: "Menunggu Activity", 110: "Revisi JR",
  150: "Menunggu Approval", 199: "Pending Client", 200: "Approved", 201: "Partial Reject",
};
const ITEM_STATUS = { pending: "Pending", approved: "Approved", rejected: "Ditolak" };

const HEADER_FIELDS = [
  ["Status", "Status", v => STATUS_LABELS[v] ?? v],
  ["WorkDate", "Tgl Pekerjaan", v => (v ?? "").slice(0, 10)],
  ["SPKNo", "SPK No"], ["WSBNo", "WSB No"], ["Priority", "Prioritas"],
  ["RequestUnit", "Request Unit"], ["PermitToWork", "Permit To Work"],
  ["Remarks", "Keterangan"], ["CQID", "CQ (ID)"],
];

// Kolom entri yang ikut dibandingkan & ditampilkan (tanpa ID/jejak waktu simpan).
const ENTRY_FIELDS = [
  "SubType", "FormulaType", "EntryDate", "DateStart", "DateEnd", "Qty", "Multiplier",
  "MeterStart", "MeterEnd", "Uom", "BilledQty", "Dimensi", "DimensiUom", "Length",
  "Height", "Weight", "RoomRef", "OccupantName", "HouseKeeping", "Remarks", "Attributes",
];

const num = v => (v === null || v === undefined || v === "" ? 0 : parseFloat(v) || 0);
const fmt = n => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(n ?? 0);
const billedOf = l => Math.max(num(l.ActualQty), num(l.MinUsage)) * num(l.ContractRate);
const norm = v => (v === null || v === undefined || v === "" ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));
const entrySig = d => ENTRY_FIELDS.map(f => norm(d[f])).join("\u0001");
const dt = v => {
  if (!v) return "";
  const s = String(v);
  return s.length > 10 ? s.slice(0, 16).replace("T", " ") : s;
};

function entryText(d, uom = {}) {
  const parts = [];
  if (d.EntryDate) parts.push(dt(d.EntryDate));
  else if (d.DateStart) parts.push(`${dt(d.DateStart)} → ${dt(d.DateEnd)}`);
  if (d.SubType) parts.push(d.SubType);
  parts.push(`Qty ${fmt(num(d.Qty))}${d.Uom ? " " + (uom[d.Uom] || d.Uom) : ""}`);
  if (num(d.BilledQty) && num(d.BilledQty) !== num(d.Qty)) parts.push(`Tagih ${fmt(num(d.BilledQty))}`);
  if (num(d.Dimensi)) parts.push(`Dimensi ${fmt(num(d.Dimensi))}${d.DimensiUom ? " " + d.DimensiUom : ""}`);
  if (d.MeterStart != null && d.MeterEnd != null && (num(d.MeterStart) || num(d.MeterEnd)))
    parts.push(`Meter ${fmt(num(d.MeterStart))}–${fmt(num(d.MeterEnd))}`);
  if (d.RoomRef) parts.push(`Room ${d.RoomRef}`);
  if (d.OccupantName) parts.push(d.OccupantName);
  if (d.Remarks) parts.push(`“${d.Remarks}”`);
  return parts.join(" · ");
}

// Selisih multiset entri: yang sama persis dihitung "tetap", sisanya hilang/baru.
function diffEntries(a = [], b = []) {
  const pool = new Map();
  a.forEach(d => { const k = entrySig(d); pool.set(k, [...(pool.get(k) ?? []), d]); });
  const added = [];
  let same = 0;
  b.forEach(d => {
    const k = entrySig(d);
    const arr = pool.get(k);
    if (arr && arr.length) { arr.pop(); same++; } else added.push(d);
  });
  const removed = [...pool.values()].flat();
  return { same, added, removed };
}

function normalize(snap) {
  const s = typeof snap === "string" ? JSON.parse(snap) : (snap ?? {});
  const lines = new Map((s.lines ?? []).map(l => [l.JrLineID, l]));
  const details = new Map();
  (s.details ?? []).forEach(d => details.set(d.JrLineID, [...(details.get(d.JrLineID) ?? []), d]));
  const total = [...lines.values()].reduce((t, l) => t + billedOf(l), 0);
  return { header: s.header ?? {}, lines, details, total };
}

function Delta({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const up = value > 0;
  return <span className={up ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>
    {up ? "+" : "−"}Rp {fmt(Math.abs(value))}
  </span>;
}

export default function JrVersionComparePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const jrID = location.state?.JrID ?? null;

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [revA, setRevA] = useState(null);
  const [revB, setRevB] = useState(null);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [openLine, setOpenLine] = useState({});

  useEffect(() => {
    if (!jrID) return;
    dispatch(getJrVersions(jrID)).unwrap()
      .then(res => {
        const d = res?.data;
        setData(d);
        const vs = d?.Versions ?? [];
        if (vs.length >= 2) {
          setRevA(vs[vs.length - 2].Revision);
          setRevB(vs[vs.length - 1].Revision);
        }
      })
      .catch(e => setError(e?.message ?? "Gagal memuat versi JR"));
  }, [jrID]); // eslint-disable-line

  const versions = data?.Versions ?? [];
  const uom = data?.UomNames ?? {};
  const vA = versions.find(v => v.Revision === revA);
  const vB = versions.find(v => v.Revision === revB);
  const A = useMemo(() => (vA ? normalize(vA.Snapshot) : null), [vA]);
  const B = useMemo(() => (vB ? normalize(vB.Snapshot) : null), [vB]);

  const rows = useMemo(() => {
    if (!A || !B) return [];
    const ids = [...new Set([...A.lines.keys(), ...B.lines.keys()])];
    return ids.map(id => {
      const a = A.lines.get(id), b = B.lines.get(id);
      const ent = diffEntries(A.details.get(id), B.details.get(id));
      const changed = !a || !b
        || num(a.ActualQty) !== num(b.ActualQty) || num(a.MinUsage) !== num(b.MinUsage)
        || num(a.ContractRate) !== num(b.ContractRate) || a.ItemStatus !== b.ItemStatus
        || norm(a.Remarks) !== norm(b.Remarks)
        || ent.added.length > 0 || ent.removed.length > 0;
      return { id, a, b, ent, changed, sort: (b ?? a).SortOrder ?? 0 };
    }).sort((x, y) => x.sort - y.sort);
  }, [A, B]);

  const headerDiff = useMemo(() => {
    if (!A || !B) return [];
    return HEADER_FIELDS
      .filter(([k]) => norm(A.header[k]) !== norm(B.header[k]))
      .map(([k, label, f]) => ({ label, a: f ? f(A.header[k]) : norm(A.header[k]), b: f ? f(B.header[k]) : norm(B.header[k]) }));
  }, [A, B]);

  const label = v => `Rev ${v.Revision}${v.IsCurrent ? " (berlaku)" : ""}`;
  const shown = onlyChanged ? rows.filter(r => r.changed) : rows;
  const changedCount = rows.filter(r => r.changed).length;

  const itemName = l => l.ItemDisplay || l.ItemName;
  const qtyText = l => l ? `${fmt(num(l.ActualQty))} ${uom[l.SatuanID] || l.SatuanID || ""}` : "—";

  if (!jrID) {
    return <div className="md:ml-64 p-10 text-center text-gray-400">JR tidak ditemukan</div>;
  }

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <span className="text-sm font-semibold" style={{ color: Colors.primary }}>Perbandingan Versi — {jrID}</span>
      </div>

      {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
      {!data && !error && <div className="p-10 text-center text-gray-400">Memuat versi…</div>}
      {data && versions.length < 2 && (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          JR ini belum pernah di-recall, jadi baru punya satu versi.
        </div>
      )}

      {data && versions.length >= 2 && (
        <>
          {/* Pilih versi */}
          <div className="bg-white rounded-xl border p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["Versi A (pembanding)", revA, setRevA], ["Versi B", revB, setRevB]].map(([t, val, set]) => (
              <div key={t}>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1">{t}</label>
                <select value={val ?? ""} onChange={e => set(parseInt(e.target.value))}
                  className="w-full border rounded px-3 py-2 text-sm bg-white">
                  {versions.map(v => <option key={v.Revision} value={v.Revision}>{label(v)}</option>)}
                </select>
                {(() => {
                  const v = versions.find(x => x.Revision === val);
                  return v && !v.IsCurrent ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Di-recall {v.RecalledAt} oleh {v.RecalledBy} — “{v.Reason}”
                    </p>
                  ) : v ? <p className="mt-1 text-xs text-gray-500">Versi yang sedang berlaku</p> : null;
                })()}
              </div>
            ))}
          </div>

          {A && B && (
            <>
              {/* Ringkasan */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs font-semibold uppercase text-gray-400">Total tagihan {label(vA)}</p>
                  <p className="text-lg font-bold text-gray-800">Rp {fmt(A.total)}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs font-semibold uppercase text-gray-400">Total tagihan {label(vB)}</p>
                  <p className="text-lg font-bold text-gray-800">Rp {fmt(B.total)}</p>
                </div>
                <div className="bg-white rounded-xl border p-4">
                  <p className="text-xs font-semibold uppercase text-gray-400">Selisih (B − A)</p>
                  <p className="text-lg"><Delta value={B.total - A.total} /></p>
                  <p className="text-xs text-gray-500">{changedCount} dari {rows.length} item berubah</p>
                </div>
              </div>

              {/* Header */}
              <div className="bg-white rounded-xl border p-5 mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">Perubahan Header</h3>
                {headerDiff.length === 0 ? (
                  <p className="text-sm text-gray-400">Tidak ada perubahan header.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500">
                      <th className="py-1 pr-4">Field</th><th className="py-1 pr-4">{label(vA)}</th><th className="py-1">{label(vB)}</th>
                    </tr></thead>
                    <tbody>{headerDiff.map(h => (
                      <tr key={h.label} className="border-t">
                        <td className="py-1.5 pr-4 text-gray-600">{h.label}</td>
                        <td className="py-1.5 pr-4 text-red-600 line-through">{h.a || "—"}</td>
                        <td className="py-1.5 text-green-700 font-medium">{h.b || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>

              {/* Item */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ background: Colors.primary + "15" }}>
                  <span className="font-semibold text-sm" style={{ color: Colors.primary }}>Perubahan Item & Activity</span>
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} />
                    Hanya yang berubah
                  </label>
                </div>
                {shown.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">Tidak ada item yang berubah.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-500">
                        <th className="px-4 py-2">Item</th>
                        <th className="px-3 py-2">Qty {label(vA)}</th>
                        <th className="px-3 py-2">Qty {label(vB)}</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2 text-right">Nilai {label(vA)}</th>
                        <th className="px-3 py-2 text-right">Nilai {label(vB)}</th>
                        <th className="px-3 py-2 text-right">Selisih</th>
                        <th className="px-3 py-2 text-center">Entri</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shown.map(r => {
                        const l = r.b ?? r.a;
                        const vaA = r.a ? billedOf(r.a) : 0, vaB = r.b ? billedOf(r.b) : 0;
                        const qtyBeda = r.a && r.b && num(r.a.ActualQty) !== num(r.b.ActualQty);
                        const entBeda = r.ent.added.length + r.ent.removed.length;
                        const open = !!openLine[r.id];
                        return (
                          <React.Fragment key={r.id}>
                            <tr className={r.changed ? "bg-amber-50/40" : ""}>
                              <td className="px-4 py-2">
                                <div className="text-gray-800">{itemName(l)}</div>
                                <div className="text-xs text-gray-400">
                                  {l.ActivityName || l.LineGroup}
                                  {!r.a && <span className="ml-1 text-green-700 font-semibold">· item baru</span>}
                                  {!r.b && <span className="ml-1 text-red-600 font-semibold">· item dihapus</span>}
                                  {r.a && r.b && num(r.a.ContractRate) !== num(r.b.ContractRate) &&
                                    <span className="ml-1">· tarif Rp {fmt(num(r.a.ContractRate))} → Rp {fmt(num(r.b.ContractRate))}</span>}
                                </div>
                              </td>
                              <td className={`px-3 py-2 ${qtyBeda ? "text-red-600 line-through" : "text-gray-600"}`}>{qtyText(r.a)}</td>
                              <td className={`px-3 py-2 ${qtyBeda ? "text-green-700 font-semibold" : "text-gray-600"}`}>{qtyText(r.b)}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">
                                {r.a && r.b && r.a.ItemStatus !== r.b.ItemStatus
                                  ? <>{ITEM_STATUS[r.a.ItemStatus] ?? r.a.ItemStatus} → <strong>{ITEM_STATUS[r.b.ItemStatus] ?? r.b.ItemStatus}</strong></>
                                  : (ITEM_STATUS[l.ItemStatus] ?? l.ItemStatus)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-600">{r.a ? `Rp ${fmt(vaA)}` : "—"}</td>
                              <td className="px-3 py-2 text-right text-gray-800">{r.b ? `Rp ${fmt(vaB)}` : "—"}</td>
                              <td className="px-3 py-2 text-right"><Delta value={vaB - vaA} /></td>
                              <td className="px-3 py-2 text-center">
                                {entBeda > 0 || r.ent.same > 0 ? (
                                  <button onClick={() => setOpenLine(o => ({ ...o, [r.id]: !open }))}
                                    className={`text-xs underline ${entBeda ? "text-orange-600" : "text-gray-500"}`}>
                                    {entBeda ? `${r.ent.removed.length} hilang · ${r.ent.added.length} baru` : `${r.ent.same} sama`}
                                  </button>
                                ) : <span className="text-xs text-gray-400">—</span>}
                              </td>
                            </tr>
                            {open && (
                              <tr><td colSpan={8} className="px-6 py-3 bg-gray-50">
                                <div className="text-xs space-y-1">
                                  {r.ent.removed.map((d, i) => (
                                    <div key={"r" + i} className="text-red-700"><span className="font-mono">−</span> {entryText(d, uom)}</div>
                                  ))}
                                  {r.ent.added.map((d, i) => (
                                    <div key={"a" + i} className="text-green-700"><span className="font-mono">+</span> {entryText(d, uom)}</div>
                                  ))}
                                  <div className="text-gray-500">{r.ent.same} entri tidak berubah.</div>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
